import {
    Component,
    OnDestroy,
    OnInit,
    ChangeDetectionStrategy,
} from '@angular/core';
import { OldSystemIframeComponent } from '../shared-components/old-system-iframe/old-system-iframe.component';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { UserService } from 'app/core/services/user.service';
import { HeadlineService } from 'app/core/services/headline.service';
import { SharedService } from 'app/core/services/shared.service';
import {
    exhaustMap,
    firstValueFrom,
    forkJoin,
    from,
    interval,
    lastValueFrom,
    Subscription,
    take,
} from 'rxjs';
import { environment } from 'environments/environment';
import { HippoConfirmationService } from '@hippo/services/confirmation';
import {
    provideTranslocoScope,
    translate,
    TranslocoModule,
    TranslocoService,
} from '@jsverse/transloco';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { NoPageAccessComponent } from '../shared-components/no-page-access/no-page-access.component';
import { LucideAngularModule } from 'lucide-angular';
import {
    ChevronLeft,
    ChevronRight,
    Circle,
    ExternalLink,
    LoaderCircle,
    Play,
    ScanSearch,
} from 'app/core/icons/lucide-icons';

interface InspectorData {
    database: {
        hd: {
            name: string;
            status: string;
        };
        hd_base: {
            name: string;
            status: string;
        };
        hd_central: {
            name: string;
            status: string;
        };
        hd_store: {
            name: string;
            status: string;
        };
    };
    date: string;
    jobs: {
        invoice_check: {
            file_status: string;
        };
    };
    logs: {
        common: {
            file_status: string;
            key: string;
        };
        database: {
            hd: {
                file_status: string;
                key: string;
            };
            hd_central: {
                file_status: string;
                key: string;
            };
            hd_store: {
                file_status: string;
                key: string;
            };
        };
    };
    timestamp: number;
}

type Tone = 'ok' | 'warn' | 'error' | 'idle';

type IndexDatabase = 'hippocentral' | 'hipposhowops';

interface IndexCheck {
    state: 'idle' | 'running' | 'ok' | 'error';
    message?: string;
    messageKey?: string;
}

const TONE_CLASS: Record<Tone, string> = {
    ok: 'text-green-500',
    warn: 'text-orange-500',
    error: 'text-red-500',
    idle: 'text-gray-400',
};

@Component({
    selector: 'app-inspector',
    imports: [
        OldSystemIframeComponent,
        CommonModule,
        MatProgressBarModule,
        NoPageAccessComponent,
        LucideAngularModule,
        TranslocoModule,
    ],
    templateUrl: './inspector.component.html',
    styleUrl: './inspector.component.scss',
    changeDetection: ChangeDetectionStrategy.Default,
    providers: [
        provideTranslocoScope('common'),
        provideTranslocoScope('codext'),
    ],
})
export class InspectorComponent implements OnInit, OnDestroy {
    data: InspectorData;
    sub: Subscription;
    environment = environment;
    date: Date | null = null;
    hasPageAccess: boolean = false;
    loading = true;
    checkedAt: Date | null = null;
    checkedDay: Date | null = null;
    indexChecks: Record<IndexDatabase, IndexCheck> = {
        hippocentral: { state: 'idle' },
        hipposhowops: { state: 'idle' },
    };

    private requestId = 0;
    private destroyed = false;

    readonly CircleIcon = Circle;
    readonly ScanSearchIcon = ScanSearch;
    readonly ChevronLeftIcon = ChevronLeft;
    readonly ChevronRightIcon = ChevronRight;
    readonly ExternalLinkIcon = ExternalLink;
    readonly LoaderIcon = LoaderCircle;
    readonly PlayIcon = Play;

    readonly logRows = [
        {
            label: 'common log',
            folder: 'common',
            status: (d: InspectorData) => d.logs.common.file_status,
        },
        {
            label: 'hippodata log',
            folder: 'hd',
            status: (d: InspectorData) => d.logs.database.hd.file_status,
        },
        {
            label: 'hippodata central log',
            folder: 'hdc',
            status: (d: InspectorData) =>
                d.logs.database.hd_central.file_status,
        },
        {
            label: 'hippodata store log',
            folder: 'hds',
            status: (d: InspectorData) => d.logs.database.hd_store.file_status,
        },
    ];

    readonly databaseRows = [
        {
            label: 'hippodata central',
            status: (d: InspectorData) => d.database.hd_central.status,
        },
        {
            label: 'hippodata showops',
            status: (d: InspectorData) => d.database.hd.status,
        },
        {
            label: 'hippodata logistics',
            status: (d: InspectorData) => d.database.hd_store.status,
        },
        {
            label: 'hippo Administration',
            status: (d: InspectorData) => d.database.hd_base.status,
        },
    ];

    readonly indexRows: { label: string; db: IndexDatabase }[] = [
        { label: 'hippodata central', db: 'hippocentral' },
        { label: 'hippodata showops', db: 'hipposhowops' },
    ];

    constructor(
        private httpClient: HttpClient,
        public userService: UserService,
        public headlineService: HeadlineService,
        private sharedService: SharedService,
        private hippoConfirmationService: HippoConfirmationService,
        private translocoService: TranslocoService
    ) {}

    async ngOnInit() {
        this.headlineService.setHeadline(['hippo Inspector'], 'inspector');
        await firstValueFrom(
            forkJoin([
                this.translocoService.selectTranslation('common').pipe(take(1)),
                this.translocoService.selectTranslation('codext').pipe(take(1)),
            ])
        );
        await this.getData();
        this.loading = false;
        this.startPolling();
    }

    get dateFormat(): string {
        return this.translocoService.getActiveLang() === 'de'
            ? 'dd.MM.yyyy'
            : 'dd/MM/yyyy';
    }

    async getData() {
        const requestId = ++this.requestId;
        const day = this.date;
        try {
            const formData = new FormData();
            formData.append('key', 'check_hippo');
            if (this.date !== null) {
                let month = (this.date.getMonth() + 1).toString();
                if (month.length === 1) {
                    month = '0' + month;
                }
                let day = this.date.getDate().toString();
                if (day.length === 1) {
                    day = '0' + day;
                }
                let dateForApi =
                    this.date.getFullYear().toString() +
                    '-' +
                    month +
                    '-' +
                    day;
                formData.append('date', dateForApi);
            }
            const data = await lastValueFrom(
                this.httpClient.post<any>(
                    `hippo/ajax_navigator.php?page=pages/administration/Ajax_inspector_state.php&uid=${this.userService.user.uid}&page_type=json`,
                    formData,
                    {
                        observe: 'response',
                    }
                )
            );
            if (requestId !== this.requestId) {
                return;
            }
            this.data = JSON.parse(JSON.stringify(data.body.result));
            this.checkedAt = this.parseDate(this.data?.date);
            this.checkedDay = day;
            this.hasPageAccess = true;
        } catch (error) {
            if (requestId !== this.requestId) {
                return;
            }
            console.log(error);
            const result = error?.error?.result;

            if (
                result === 'page access denied' ||
                result?.message === 'page access denied'
            ) {
                this.hasPageAccess = false;
            } else {
                this.sharedService.showDataNotLoaded(
                    error,
                    'InspectorComponent',
                    'getData'
                );
            }
        }
    }

    logTone(status: string): Tone {
        switch (status) {
            case 'ok':
            case 'no_file':
                return 'ok';
            case 'warn':
                return 'warn';
            default:
                return 'error';
        }
    }

    invoiceTone(status: string): Tone {
        return status === 'ok' ? 'ok' : 'error';
    }

    indexTone(db: IndexDatabase): Tone {
        const state = this.indexChecks[db].state;
        return state === 'ok' || state === 'error' ? state : 'idle';
    }

    toneClass(tone: Tone): string {
        return TONE_CLASS[tone];
    }

    statusLabel(status: string): string {
        const key =
            {
                ok: 'insStatusOk',
                no_file: 'insStatusNoFile',
                warn: 'insStatusWarn',
            }[status] ?? 'insStatusError';
        return translate(`codext.${key}`);
    }

    invoiceLabel(status: string): string {
        const key =
            {
                ok: 'insStatusOk',
                error: 'insStatusError',
            }[status] ?? 'insStatusMissing';
        return translate(`codext.${key}`);
    }

    async openLogFileWindow(folder: string) {
        try {
            let date = Number.isInteger(this.data.timestamp)
                ? this.sharedService.toUtcDateFromTimestamp(
                      new Date(this.data.timestamp * 1000)
                  )
                : new Date();
            window.open(
                `${environment.apiURL}navigator.php?page=pages/info/System_log_check.php&uid=${this.userService.user.uid}&f=${folder}&y=${date.getFullYear().toString()}&m=${('0' + (date.getMonth() + 1).toString()).slice(-2)}&d=${('0' + date.getDate().toString()).slice(-2)}&pwd=`,
                '_blank'
            );
        } catch (error) {
            console.log(error);
        }
    }

    async checkIndexes(db: IndexDatabase) {
        if (this.indexChecks[db].state === 'running') {
            return;
        }
        this.indexChecks[db] = { state: 'running' };
        try {
            const response = await lastValueFrom(
                this.httpClient.get(
                    `hippo/ajax_navigator.php?page=pages/administration/Ajax_inspector_check_indexes.php&uid=${this.userService.user.uid}&db=${db}`,
                    { responseType: 'text' }
                )
            );
            const message = response
                .replace(/<[^>]*>/g, ' ')
                .replace(/\s+/g, ' ')
                .trim();
            if (/^all indexes valid/i.test(message)) {
                this.indexChecks[db] = {
                    state: 'ok',
                    messageKey: 'codext.insIndexesValid',
                };
            } else if (!message || /fatal error|<html/i.test(response)) {
                this.indexChecks[db] = {
                    state: 'error',
                    messageKey: 'codext.insIndexCheckFailed',
                };
            } else {
                this.indexChecks[db] = {
                    state: 'error',
                    message:
                        message.length > 240
                            ? message.slice(0, 240) + ' …'
                            : message,
                };
            }
        } catch {
            this.indexChecks[db] = {
                state: 'error',
                messageKey: 'codext.insIndexCheckFailed',
            };
        }
    }

    requestInvoiceInformation() {
        const confirmation = this.hippoConfirmationService.open({
            title: '',
            message: translate('codext.wantStartInvoiceAudit'),
            icon: {
                show: true,
                color: 'warn',
                name: 'heroicons_outline:question-mark-circle',
            },
            actions: {
                confirm: {
                    label: translate('common.yes'),
                    color: 'accent',
                },
                cancel: {
                    show: true,
                    label: translate('common.no'),
                },
            },
            dismissible: false,
        });
        confirmation.afterClosed().subscribe(async result => {
            if (result === 'confirmed') {
                window.open(
                    `${environment.apiURL}cron_navigator.php?page=event_scheduling/CJ_invoice_information.php&uid=${this.userService.user.uid}&option=R&debug_mode=2`,
                    '_blank'
                );
            }
        });
    }

    shiftDay(days: number) {
        const next = new Date(this.date ?? new Date());
        next.setDate(next.getDate() + days);
        if (
            next.toDateString() === new Date().toDateString() ||
            next > new Date()
        ) {
            this.resetTimestamp();
            return;
        }
        this.sub?.unsubscribe();
        this.date = next;
        this.getData();
    }

    async resetTimestamp() {
        this.date = null;
        await this.getData();
        if (this.date === null) {
            this.startPolling();
        }
    }

    private parseDate(value: string | undefined): Date | null {
        const date = value ? new Date(value.replace(' ', 'T')) : null;
        return date && !isNaN(date.getTime()) ? date : null;
    }

    private startPolling() {
        this.sub?.unsubscribe();
        if (this.destroyed) {
            return;
        }
        this.sub = interval(10000)
            .pipe(exhaustMap(() => from(this.getData())))
            .subscribe();
    }

    ngOnDestroy(): void {
        this.destroyed = true;
        this.sub?.unsubscribe();
    }
}
